import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, X, Lightbulb, Sparkle, HelpCircle, ChevronDown, MessageSquare, Play, PartyPopper, ClipboardCheck } from 'lucide-react';
import { ThemeId, themeById, MODULES, loadProgress, saveThemeState } from '../data/learning';
import { getLessonParts } from '../data/content';
import { LessonCheck, CompareTable, LessonVideo } from '../data/content/types';
import { playCorrect, playWrong, playDone } from '../utils/sound';
import { SoundToggle } from '../components/ui/SoundToggle';

// Inline kennischeck: kies een antwoord, krijg meteen uitleg (met zachte geluidsfeedback).
const InlineCheck: React.FC<{ check: LessonCheck; accent: string }> = ({ check, accent }) => {
  const [picked, setPicked] = useState<number | null>(null);
  const answered = picked !== null;
  const pick = (i: number) => {
    setPicked(i);
    if (i === check.correct) playCorrect(); else playWrong();
  };
  return (
    <div className="card p-5 my-6" style={{ borderTop: `3px solid ${accent}` }}>
      <p className="label mb-3 inline-flex items-center gap-1.5" style={{ color: accent }}>
        <HelpCircle className="w-4 h-4" strokeWidth={2} /> Even checken
      </p>
      <p className="font-display font-semibold text-ink mb-4" style={{ fontSize: 18, lineHeight: 1.3 }}>{check.q}</p>
      <div className="flex flex-col gap-2.5 mb-1">
        {check.opts.map((opt, i) => {
          const isCorrect = i === check.correct;
          const isPicked = i === picked;
          let bg = '#fff', border = '#E7E2DB', icon: React.ReactNode = null;
          if (answered) {
            if (isCorrect) { bg = '#E9F4EE'; border = '#2B7A4B'; icon = <Check className="w-4 h-4" style={{ color: '#2B7A4B' }} strokeWidth={2.5} />; }
            else if (isPicked) { bg = '#FBE9EC'; border = '#8A2C3D'; icon = <X className="w-4 h-4" style={{ color: '#8A2C3D' }} strokeWidth={2.5} />; }
          }
          return (
            <button key={i} disabled={answered} onClick={() => pick(i)}
              className="flex items-center gap-3 text-left rounded-lg p-3.5 border transition-colors"
              style={{ background: bg, borderColor: isPicked && !answered ? accent : border, cursor: answered ? 'default' : 'pointer' }}>
              <span className="grid place-items-center rounded-full flex-none border-2" style={{ width: 22, height: 22, borderColor: answered && (isCorrect || isPicked) ? border : '#CFC5B2' }}>
                {icon}
              </span>
              <span className="text-ink" style={{ fontSize: 16 }}>{opt}</span>
            </button>
          );
        })}
      </div>
      {answered && (
        <div className="rounded-lg p-4 mt-3 anim-pop" style={{ background: '#F4F1EC' }}>
          <p className="text-sm" style={{ color: '#33302B' }}>
            <strong className="text-ink">{picked === check.correct ? 'Klopt. ' : 'Net niet. '}</strong>{check.explain}
          </p>
        </div>
      )}
    </div>
  );
};

// Inklapbare verdieping voor wie meer wil.
const Expand: React.FC<{ title: string; body: string[] }> = ({ title, body }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="card my-6 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center justify-between w-full p-4 text-left">
        <span className="font-display font-semibold text-ink inline-flex items-center gap-2" style={{ fontSize: 16.5 }}>
          <Sparkle className="w-4 h-4 flex-none" style={{ color: '#8A6D12' }} strokeWidth={1.9} /> {title}
        </span>
        <ChevronDown className="w-5 h-5 flex-none text-sub transition-transform" strokeWidth={2} style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div className="px-4 pb-4 -mt-1 lesson-prose anim-rise" style={{ fontSize: 16.5 }}>
          {body.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
    </div>
  );
};

const CompareGrid: React.FC<{ table: CompareTable }> = ({ table }) => (
  <div className="my-6">
    {table.title && <p className="font-display font-semibold text-ink mb-3" style={{ fontSize: 17 }}>{table.title}</p>}
    <div className="card overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontSize: 14.5 }}>
        <thead>
          <tr>
            {table.headers.map((h, i) => (
              <th key={i} className="text-left font-display font-semibold text-ink p-3 align-bottom" style={{ background: '#F4F1EC', borderBottom: '2px solid #E7E2DB', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r} style={{ borderBottom: '1px solid #EEE9E1' }}>
              {row.map((cell, c) => (
                <td key={c} className={c === 0 ? 'p-3 font-medium text-ink align-top' : 'p-3 text-sub align-top'} style={{ minWidth: c === 0 ? 120 : 140 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Video die pas laadt na een klik (youtube-nocookie): snel én privacyvriendelijk.
const LiteVideo: React.FC<{ video: LessonVideo; accent: string }> = ({ video, accent }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="my-6">
      <div className="card overflow-hidden" style={{ position: 'relative', aspectRatio: '16 / 9', background: '#17130E' }}>
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <button onClick={() => setPlaying(true)} aria-label={`Video afspelen: ${video.title}`} className="group" style={{ position: 'absolute', inset: 0, width: '100%', cursor: 'pointer' }}>
            <img
              src={`https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`}
              alt=""
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
            />
            <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(23,19,14,.55), rgba(23,19,14,.05))' }} />
            <span className="grid place-items-center rounded-full transition-transform group-hover:scale-105" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 68, height: 68, background: accent, boxShadow: '0 10px 30px rgba(0,0,0,.35)' }}>
              <Play className="w-7 h-7" style={{ color: '#fff', marginLeft: 4 }} fill="#fff" strokeWidth={0} />
            </span>
            <span className="font-display font-semibold text-white text-left" style={{ position: 'absolute', left: 18, right: 18, bottom: 14, fontSize: 16.5, textShadow: '0 1px 8px rgba(0,0,0,.5)' }}>
              {video.title}
            </span>
          </button>
        )}
      </div>
      {video.caption && <p className="text-sm text-sub mt-2.5 flex items-start gap-2"><Play className="w-3.5 h-3.5 mt-1 flex-none" style={{ color: accent }} strokeWidth={2} /> {video.caption}</p>}
    </div>
  );
};

export const Lesson: React.FC = () => {
  const { themeId, moduleIdx } = useParams<{ themeId: string; moduleIdx: string }>();
  const navigate = useNavigate();
  const t = themeById((themeId as ThemeId) || 'ai');
  const mIdx = parseInt(moduleIdx || '0', 10);
  const mods = MODULES[t.id];
  const mod = mods[mIdx];
  const parts = getLessonParts(t.id, mIdx);
  const count = parts.length || (parseInt(mod.sub, 10) || 1);

  const [part, setPart] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => { setPart(0); setCelebrate(false); window.scrollTo(0, 0); }, [themeId, moduleIdx]);
  const cur = parts[part];
  const lastPart = part === count - 1;
  const hasNextModule = mIdx + 1 < mods.length;

  const goPart = (p: number) => { setPart(p); window.scrollTo(0, 0); };

  const completeModule = () => {
    const done = loadProgress()[t.id].doneModules;
    saveThemeState(t.id, { doneModules: Math.max(done, mIdx + 1) });
    setCelebrate(true);
    playDone();
  };

  const doneAfter = Math.max(loadProgress()[t.id].doneModules, mIdx + 1);

  return (
    <div className="shell py-12 sm:py-16">
      <div className="flex items-center justify-between mb-5">
        <Link to={`/thema/${t.id}`} className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-ink">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> {t.name}
        </Link>
        <SoundToggle />
      </div>

      {/* Voortgang binnen de module */}
      <div className="flex items-center gap-3 mb-7">
        <div className="flex-1">
          <span className="block w-full rounded-full bg-line overflow-hidden" style={{ height: 8 }}>
            <span className="block h-full rounded-full" style={{ width: `${((part + 1) / count) * 100}%`, background: t.accent, transition: 'width .45s cubic-bezier(.2,.7,.2,1)' }} />
          </span>
        </div>
        <span className="text-sm text-sub tabular-nums flex-none">Onderdeel {part + 1} / {count}</span>
      </div>

      <div className="flex flex-wrap gap-8">
        {/* Hoofdcontent */}
        <article className="flex-1" style={{ flexBasis: 460 }}>
          <p className="label mb-2" style={{ color: t.accent }}>Module {mIdx + 1} van {mods.length}</p>
          <h1 className="font-display font-bold text-ink mb-1" style={{ fontSize: 'clamp(26px,4vw,36px)', lineHeight: 1.15 }}>{mod.title}</h1>

          {/* Per onderdeel een rustige binnenkom-animatie */}
          <div key={part} className="anim-rise">
            {cur && <p className="font-display font-semibold text-sub mb-6" style={{ fontSize: 21 }}>{cur.heading}</p>}

            {cur ? (
              <div className="lesson-prose">
                {cur.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            ) : (
              <div className="lesson-prose"><p>De inhoud van dit onderdeel volgt binnenkort.</p></div>
            )}

            {/* Video */}
            {cur?.video && <LiteVideo video={cur.video} accent={t.accent} />}

            {/* Vergelijkingstabel */}
            {cur?.compare && <CompareGrid table={cur.compare} />}

            {/* Wist je dat */}
            {cur?.didYouKnow && (
              <div className="rounded-lg p-4 my-6 flex items-start gap-3" style={{ background: '#F6EFD9' }}>
                <Sparkle className="w-5 h-5 mt-0.5 flex-none" style={{ color: '#8A6D12' }} strokeWidth={1.9} />
                <p className="text-sm" style={{ color: '#33302B' }}><strong className="text-ink">Wist je dat — </strong>{cur.didYouKnow}</p>
              </div>
            )}

            {/* Verdieping (inklapbaar) */}
            {cur?.expand && <Expand title={cur.expand.title} body={cur.expand.body} />}

            {/* Inline kennischeck */}
            {cur?.check && <InlineCheck check={cur.check} accent={t.accent} />}

            {/* Reflectievraag */}
            {cur?.reflect && (
              <div className="card p-5 my-6" style={{ background: '#F4F1EC' }}>
                <p className="label mb-2 inline-flex items-center gap-1.5 text-sub"><MessageSquare className="w-4 h-4" strokeWidth={1.9} /> Even voor jezelf</p>
                <p className="text-ink" style={{ fontSize: 16.5, lineHeight: 1.5 }}>{cur.reflect}</p>
              </div>
            )}

            {/* Kernpunten-callout */}
            {cur?.keyPoints?.length ? (
              <div className="card p-5 my-6" style={{ borderLeft: '3px solid #C29A33' }}>
                <p className="font-display font-semibold text-ink mb-2" style={{ fontSize: 15.5 }}>Onthoud dit</p>
                <ul className="text-sub text-sm flex flex-col gap-1.5">
                  {cur.keyPoints.map((it, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 flex-none" style={{ color: '#C29A33' }} strokeWidth={2} /> {it}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Tip */}
            {cur?.tip && (
              <div className="rounded p-4 my-5 flex items-start gap-2.5" style={{ background: '#EAEEFC' }}>
                <Lightbulb className="w-4 h-4 mt-0.5 flex-none" style={{ color: '#2440CF' }} strokeWidth={1.75} />
                <p className="text-sm" style={{ color: '#33302B' }}><strong className="text-ink">Tip — </strong>{cur.tip}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-hairline pt-5 mt-6">
            <button onClick={() => goPart(Math.max(0, part - 1))} className="btn-outline" style={{ visibility: part === 0 ? 'hidden' : 'visible' }}>
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Vorige
            </button>
            <button onClick={() => (lastPart ? completeModule() : goPart(part + 1))} className="btn-primary">
              {lastPart ? 'Module afronden' : 'Volgende onderdeel'} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.75} />
            </button>
          </div>
        </article>

        {/* Zijbalk */}
        <aside className="flex-1" style={{ flexBasis: 240, maxWidth: 320 }}>
          <div className="card p-5 lg:sticky" style={{ top: 90 }}>
            <p className="label mb-3">Onderdelen</p>
            <ul className="flex flex-col gap-1">
              {Array.from({ length: count }).map((_, i) => {
                const cdone = i < part, isCur = i === part;
                const label = parts[i]?.heading || `Onderdeel ${i + 1}`;
                return (
                  <li key={i}>
                    <button onClick={() => goPart(i)} className="flex items-center gap-3 py-1.5 text-left w-full">
                      <span className="grid place-items-center rounded-full flex-none border-2" style={{ width: 22, height: 22, borderColor: cdone ? '#C29A33' : isCur ? t.accent : '#CFC5B2', background: isCur ? t.accent : 'transparent' }}>
                        {cdone ? <Check className="w-3 h-3" style={{ color: '#C29A33' }} strokeWidth={3} /> : <span className="text-[10px] font-semibold" style={{ color: isCur ? '#fff' : '#9A938A' }}>{i + 1}</span>}
                      </span>
                      <span className="text-sm" style={{ color: isCur ? '#17130E' : '#6B6258' }}>{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>

      {/* Module afgerond: vier het even */}
      {celebrate && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(23,19,14,.45)', zIndex: 50, display: 'grid', placeItems: 'center', padding: 20 }} onClick={() => navigate(`/thema/${t.id}`)}>
            <div className="card p-8 text-center anim-pop" style={{ maxWidth: 480, width: '100%' }} onClick={(e) => e.stopPropagation()}>
              <span className="grid place-items-center rounded-full mx-auto mb-5" style={{ width: 64, height: 64, background: t.tint }}>
                <PartyPopper className="w-8 h-8" style={{ color: t.accent }} strokeWidth={1.75} />
              </span>
              <p className="label mb-2" style={{ color: t.accent }}>Module afgerond</p>
              <h2 className="font-display font-bold text-ink mb-2" style={{ fontSize: 26, lineHeight: 1.15 }}>{mod.title} zit erop.</h2>
              <p className="text-sub mb-6">Je hebt nu {doneAfter} van de {mods.length} modules van {t.name} afgerond.{!hasNextModule && doneAfter >= mods.length ? ' Dat was de laatste. Tijd om te laten zien wat je weet.' : ''}</p>
              <div className="flex flex-col gap-2.5">
                {hasNextModule ? (
                  <button onClick={() => navigate(`/thema/${t.id}/les/${mIdx + 1}`)} className="btn-primary w-full">
                    Volgende module: {mods[mIdx + 1].title} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={1.75} />
                  </button>
                ) : (
                  <button onClick={() => navigate(`/thema/${t.id}/toets`)} className="btn-primary w-full">
                    <ClipboardCheck className="w-4 h-4" strokeWidth={1.75} /> Start de toets en haal je certificaat
                  </button>
                )}
                <button onClick={() => navigate(`/thema/${t.id}`)} className="btn-outline w-full">Terug naar het thema</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
